import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import { Dropdown, Tooltip } from "antd"
import type { DropdownProps } from "antd"
import type { ItemType } from "antd/es/menu/hooks/useItems"
import React, { type ReactNode } from "react"

/**
 * Cell building blocks for the table design.
 *
 * The icon group and the action menu stay separate components: whether a table
 * shows a row of direct icon controls or a labelled menu is a product decision
 * made per table, not inferred from its contents.
 *
 * Within the menu, though, the count is inferred. A menu that would open onto a
 * single action renders that action as its own button instead, so a row never
 * asks for a click only to offer one choice. See `resolveSingleAction`.
 */

/* ------------------------------------------------------------ identity -- */

/* ---------------------------------------------------------- icon button -- */

/**
 * The round icon button rows use.
 *
 * Figma `cell icon` (node 893:7483): 24px slate-100 circle holding a 10px
 * slate-900 glyph at 1.8 stroke. It lived inside `TableIdentityCell`, which
 * meant every other row affordance - copy an id, open a timeline - got
 * hand-rolled instead, usually smaller and usually without a hover state.
 *
 * The glyph is passed as a child and should draw with `currentColor`, so it
 * follows the button through hover and focus. An `<img>` cannot: its colour
 * lives in the file, not the DOM.
 */
export const TableIconButton = ({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: ReactNode
}) => (
  <Tooltip title={label}>
    <button
      type="button"
      className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#f1f5f9] text-[#0f172b] transition-colors hover:bg-[#e2e8f0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#004af5]"
      onClick={(event) => {
        // Rows can sit inside a clickable container; the button's job is its
        // own, not the container's.
        event.stopPropagation()
        onClick()
      }}
      aria-label={label}
    >
      {children}
    </button>
  </Tooltip>
)

/** The caret used to open a record. */
export const ChevronGlyph = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 4.925 8.05"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M0.9 0.9L4.025 4.025L0.9 7.15"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export const TableIdentityCell = ({
  avatar,
  name,
  onView,
  viewLabel = "View details",
}: {
  /** Rendered as-is, so a call site can pass its own initials avatar. */
  avatar?: ReactNode
  name: ReactNode
  /** Omit to hide the row-view chevron. */
  onView?: () => void
  viewLabel?: string
}) => (
  <div className="app-table-identity">
    {avatar}
    <span className="app-table-identity__name">{name}</span>
    {onView ? (
      <TableIconButton
        label={viewLabel}
        onClick={onView}
      >
        <ChevronGlyph />
      </TableIconButton>
    ) : null}
  </div>
)

/* --------------------------------------------------- action icon group -- */

export type TableAction = {
  key: string
  icon: ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
}

/**
 * The icon variant: 36x36 hit boxes with a hairline divider between each.
 * Dividers come from CSS (`.app-table-actions__item + .app-table-actions__item`)
 * so hiding an action never leaves an orphaned rule behind it.
 */
export const TableActions = ({ actions }: { actions: TableAction[] }) => (
  <div className="app-table-actions">
    {actions.map((action) => (
      // aria-disabled rather than disabled: a disabled button receives no
      // pointer events, so its tooltip would vanish exactly when someone hovers
      // to find out what the greyed icon is. Wrapping it in a span instead would
      // break the `item + item` sibling rule that draws the dividers.
      <Tooltip
        key={action.key}
        title={action.label}
      >
        <button
          type="button"
          className="app-table-actions__item"
          onClick={(event) => {
            event.stopPropagation()
            if (action.disabled) return
            action.onClick()
          }}
          aria-disabled={action.disabled || undefined}
          aria-label={action.label}
        >
          {action.icon}
        </button>
      </Tooltip>
    ))}
  </div>
)

/* ------------------------------------------------------ action trigger -- */

/**
 * The pill on its own, for the rows that build their menu inline and so cannot
 * hand a ready `items` array to `TableActionSelect`. Same element either way,
 * so a screen that mixes the two forms still renders one affordance.
 *
 * Pill per Figma `button` (node 893:63195): slate-100 fill, 1.4px slate-200
 * border, slate-500 semibold label, fully rounded, 6/10 padding.
 */
export const TableActionTrigger = React.forwardRef<
  HTMLButtonElement,
  {
    label?: string
    disabled?: boolean
  } & React.ComponentPropsWithoutRef<"button">
>(({ label = "Select action", disabled, onClick, ...rest }, ref) => (
  <button
    {...rest}
    ref={ref}
    type="button"
    disabled={disabled}
    onClick={(event) => {
      // The row itself is often clickable; opening the menu must not also
      // navigate away from the row the menu belongs to.
      event.stopPropagation()
      onClick?.(event)
    }}
    className={ACTION_PILL_CLASS}
  >
    {label}
    <ChevronDownIcon className="h-4 w-4" />
  </button>
))
TableActionTrigger.displayName = "TableActionTrigger"

/* ----------------------------------------------------- approval status -- */

/** Request states, as the API numbers them. */
export const APPROVAL_STATUS = {
  approved: 1,
  declined: 2,
  pendingReview: 3,
} as const

/**
 * Where a request has got to in its approval chain.
 *
 * A settled request is one thing - Approved, Declined - and fits a pill. A
 * pending one is two: the state, and who it is sitting with. Four of the five
 * tables were putting both in the pill, so "Pending with Jane Doe" came out as
 * one long line that stretched the column and read as a status name. The
 * violations table already split them; this is that treatment, shared, so the
 * five stay in step.
 */
export type StatusTone =
  | "success"
  | "danger"
  | "pending"
  | "warning"
  | "neutral"

const STATUS_TONE_CLASS: Record<StatusTone, string> = {
  success: "rounded px-3 py-0.5 bg-[#C1EFDA] text-[#1F704B]",
  danger: "rounded px-3 py-0.5 bg-[#FBE4E1] text-[#EB3949]",
  pending:
    "rounded border-2 border-[#E69702] bg-[#FEE6CA] px-4 py-1 text-neutral800",
  warning: "rounded px-3 py-0.5 bg-[#FEF3E2] text-[#B54708]",
  neutral: "rounded px-3 py-0.5 bg-[#f1f5f9] text-[#62748e]",
}

/**
 * One status chip for every request table, whatever the request. Tables that drew their own
 * - bordered boxes, antd outline tags, a second copy of the label underneath - read as
 * different products side by side; this is the look the approval tables already use.
 */
export const StatusChip = ({
  tone,
  label,
  note,
  footnote,
}: {
  tone: StatusTone
  label: ReactNode
  /** A short line under the chip: who it is with, how far through approval. */
  note?: ReactNode
  footnote?: ReactNode
}) => (
  <div className="w-fit">
    <span
      className={`inline-block whitespace-nowrap font-avenir text-xs font-medium ${STATUS_TONE_CLASS[tone]}`}
    >
      {label}
    </span>
    {note ? <div className="mt-1 text-xs text-neutral500">{note}</div> : null}
    {footnote}
  </div>
)

export const ApprovalStatusCell = ({
  statusId,
  label,
  reviewStatus,
  footnote,
}: {
  /** `employee_request_status_id`. String or number - the APIs disagree. */
  statusId: string | number | null | undefined
  /** `employee_request_status`: the settled wording, "Approved" / "Declined". */
  label?: string | null
  /** `review_status`: who the request is pending with. */
  reviewStatus?: string | null
  /** An extra note under the status, e.g. a deactivated employee. */
  footnote?: ReactNode
}) => {
  const id = Number(statusId)

  if (id === APPROVAL_STATUS.pendingReview) {
    // Self-service lists send "Pending Review" as the reviewer line when nobody is named,
    // which printed the label twice.
    const note =
      reviewStatus && reviewStatus.trim().toLowerCase() !== "pending review"
        ? reviewStatus
        : null
    return (
      <StatusChip
        tone="pending"
        label="Pending Review"
        note={note}
        footnote={footnote}
      />
    )
  }

  return (
    <StatusChip
      tone={
        id === APPROVAL_STATUS.approved
          ? "success"
          : id === APPROVAL_STATUS.declined
          ? "danger"
          : "neutral"
      }
      label={label}
      footnote={footnote}
    />
  )
}

/**
 * The look shared by request tables beyond the columns: the same row height and cell text
 * colour as the leave requests table. Lists of short text rows looked cramped beside it,
 * because its rows are held open by an avatar and an action button, and some lists printed
 * cells in near-black while it used the grey. Applied to the table's wrapper.
 */
export const REQUEST_TABLE_CLASS =
  // Data rows only. antd puts a hidden measure row at the top of a scrolling table's body;
  // holding every td open gave that row the full height too, a blank band above the first row.
  "[&_.ant-table-tbody_tr.ant-table-row_td]:!h-20 [&_.ant-table-tbody_td]:!text-[#42526D]"

/* --------------------------------------------------------- action menu -- */

/**
 * Dismiss an open menu as soon as anything under it scrolls.
 *
 * The menu is portalled to `<body>`, which puts it outside the table's
 * stacking context - so once it is open it paints over everything, including
 * the frozen column its own row has just slid beneath. Nothing the table can
 * set will make a portal on `<body>` pass under a cell inside the table.
 *
 * Rather than fight that, the menu closes: it is anchored to a row, and once
 * that row has moved the menu no longer points at anything. Capture phase,
 * because the scroll that matters is the table body's own and scroll events do
 * not bubble.
 */
export const subscribeToScrollDismissal = (
  close: () => void,
  target: Pick<Document, "addEventListener" | "removeEventListener"> = document
) => {
  // Capture phase is the whole point: the scroll that matters is the table
  // body's own, and scroll does not bubble - a listener on window never sees
  // it, and the menu goes on floating over the frozen column. Moving this to
  // the bubble phase looks equivalent and silently restores the bug, which is
  // why it is a named export with a test rather than an inline effect.
  target.addEventListener("scroll", close, true)
  return () => target.removeEventListener("scroll", close, true)
}

const useCloseOnScroll = (open: boolean, close: () => void) => {
  React.useEffect(
    () => (open ? subscribeToScrollDismissal(close) : undefined),
    [open, close]
  )
}

const ACTION_PILL_CLASS =
  "app-table-action-select inline-flex items-center gap-1 whitespace-nowrap rounded-full border-[1.4px] border-[#e2e8f0] bg-[#f1f5f9] px-[10px] py-[6px] text-sm font-semibold leading-[1.5] text-[#62748e] transition-colors hover:bg-[#e2e8f0] disabled:opacity-40"

/**
 * A lone action is a pill of its own, the same size and shape as the menu pill beside it in
 * other rows, so a column of actions reads as one control whichever kind a row has.
 *
 * Distinguished from the menu by colour and by its chevron: brand-tinted rather than grey,
 * and pointing right ("go") rather than down ("choose"). It was plain blue words before,
 * which in a wide column read as detached text rather than as something to press.
 *
 * Its label often brings the grey text class it was given for the dropdown menu, so
 * descendants inherit the pill's colour. The tints are mixed from the brand colour, so a
 * white-label account gets its own.
 */
const SINGLE_ACTION_CLASS =
  "app-table-action-select group/single-action inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border-[1.4px] border-primary100 bg-primary50 px-[10px] py-[6px] text-sm font-semibold leading-[1.5] text-primary500 transition-colors hover:border-primary500/40 hover:bg-primary100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary500 disabled:cursor-not-allowed disabled:opacity-40 [&_*]:!text-inherit"

const SINGLE_ACTION_CHEVRON = (
  <ChevronRightIcon
    className="h-4 w-4 transition-transform group-hover/single-action:translate-x-0.5"
    aria-hidden="true"
  />
)

/**
 * The one action a row menu would offer, when that is all it would offer.
 *
 * A menu that opens onto a single choice costs a click and says nothing, so a
 * lone action is shown as its own button instead. Returns null wherever a menu
 * is still the right control:
 *
 *  - more than one real action, or a custom `dropdownRender` we cannot inspect
 *  - a submenu, which by definition hides further choices
 *  - a destructive action: one click on Delete in every row is too easy to hit
 *    by accident, and the menu's extra step is the protection
 *
 * Call sites often build items conditionally (`[canEdit && edit, view]`) and
 * add dividers, so only items that would actually render are counted.
 */
export const resolveSingleAction = (
  menu: DropdownProps["menu"],
  dropdownRender?: DropdownProps["dropdownRender"]
): Exclude<ItemType, null> | null => {
  if (dropdownRender || !menu?.items) return null
  const actionable = menu.items.filter(
    (item): item is Exclude<ItemType, null> =>
      Boolean(item) &&
      (item as any).type !== "divider" &&
      (item as any).type !== "group"
  )
  if (actionable.length !== 1) return null
  const [only] = actionable as any[]
  if (only.children?.length || only.danger) return null
  return only
}

/**
 * Fires a lone action the way the menu would have. Returns false when the item
 * carries no handler of its own, which means its click lives inside its label
 * (a button or link rendered as the label) and has to be left to that element.
 */
export const dispatchSingleAction = (
  item: any,
  menuOnClick: ((info: any) => void) | undefined,
  domEvent: React.MouseEvent | React.KeyboardEvent
) => {
  const handler = item?.onClick ?? menuOnClick
  if (!handler) return false
  handler({
    key: String(item.key),
    keyPath: [String(item.key)],
    item,
    domEvent,
  })
  return true
}

const TableSingleAction = ({
  item,
  menuOnClick,
  disabled,
}: {
  item: any
  menuOnClick?: (info: any) => void
  disabled?: boolean
}) => {
  const isDisabled = Boolean(disabled || item.disabled)
  const content = (
    <>
      {item.icon}
      {item.label}
      {SINGLE_ACTION_CHEVRON}
    </>
  )

  // The click is inside the label itself. A span keeps that element the target
  // rather than nesting a button inside a button.
  if (!item.onClick && !menuOnClick) {
    return (
      <span
        className={`${SINGLE_ACTION_CLASS}${
          isDisabled ? " pointer-events-none opacity-40" : ""
        }`}
        onClick={(event) => {
          // The row is often clickable; acting on it must not also open the row.
          event.stopPropagation()

          // Now that this is a pill, its padding and chevron are part of the target. Only
          // the link or button in the label knows what to do, so a click anywhere else in
          // the pill is handed to it.
          //
          // Only for clicks that really landed in the pill. React bubbles clicks from a portal
          // up the component tree, so a label that opens a modal would otherwise have every
          // click inside that modal forwarded back here and reopen it.
          const pill = event.currentTarget
          const target = event.target as Node
          if (!pill.contains(target)) return

          const inner = pill.querySelector<HTMLElement>("a, button")
          if (inner && !inner.contains(target)) {
            inner.click()
          }
        }}
      >
        {content}
      </span>
    )
  }

  return (
    <button
      type="button"
      className={SINGLE_ACTION_CLASS}
      disabled={isDisabled}
      onClick={(event) => {
        event.stopPropagation()
        dispatchSingleAction(item, menuOnClick, event)
      }}
    >
      {content}
    </button>
  )
}

/**
 * The row action menu: the pill, its dropdown, and the dismissal behaviour.
 *
 * Takes the same props as antd's `Dropdown` minus its child, so a caller that
 * was building the dropdown by hand can pass its existing `menu` straight
 * through. A menu holding a single action renders that action as a button
 * instead - see `resolveSingleAction`.
 */
export const TableActionMenu = ({
  label,
  disabled,
  ...dropdown
}: Omit<DropdownProps, "children" | "open" | "onOpenChange"> & {
  label?: string
}) => {
  const [open, setOpen] = React.useState(false)
  const close = React.useCallback(() => setOpen(false), [])
  useCloseOnScroll(open, close)

  const single = resolveSingleAction(dropdown.menu, dropdown.dropdownRender)
  if (single) {
    return (
      <TableSingleAction
        item={single}
        menuOnClick={dropdown.menu?.onClick}
        disabled={disabled}
      />
    )
  }

  return (
    <Dropdown
      trigger={["click"]}
      {...dropdown}
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
    >
      <TableActionTrigger
        label={label}
        disabled={disabled}
      />
    </Dropdown>
  )
}

/* ------------------------------------------------------- action select -- */

/**
 * The menu variant. Replaces the trailing ellipsis button: the design moves
 * this to a labelled control near the start of the row, so the affordance
 * reads as "there are actions here" rather than as an icon to decode.
 */
export const TableActionSelect = ({
  items,
  label = "Select action",
  disabled,
}: {
  items: ItemType[]
  label?: string
  disabled?: boolean
}) => (
  <TableActionMenu
    menu={{ items }}
    label={label}
    disabled={disabled}
  />
)
